import { useMemo } from 'react'
import { differenceInDays } from 'date-fns'

import Text, { TextProps } from 'components/Text'
import Tooltip, { PositionType } from 'components/Tooltip'

import { formatDatetime, humanize, parseDate } from 'utils/datetime'
import { DateFormat } from 'types/global'

type Props = Omit<TextProps, 'children'> & {
  date?: string | Date | null
  dateFormat?: DateFormat
  position?: PositionType
  showHumanized?: boolean
  showTooltip?: boolean
  isDateOnly?: boolean
  isUnstyled?: boolean
}

// Future dates and dates older than 7 days already shows datetime, no need for tooltip
const getTooltipDate = ({ date, dateFormat = 'PPPpp' }: Props) => {
  const now = new Date()
  const parsedDate = parseDate(date)

  if (parsedDate) {
    const diff = now ? differenceInDays(now, parsedDate) : NaN
    return diff > 7 || diff < 0 ? null : formatDatetime(parsedDate, dateFormat)
  }

  return null
}

const DateWithToolTip = ({
  date,
  dateFormat,
  position,
  showHumanized = false,
  showTooltip = true,
  textAlign,
  isDateOnly = false,
  isUnstyled = false,
  ...textProps
}: Props) => {
  const { displayDatetime, tooltipDate } = useMemo(() => {
    if (!date) {
      return {
        displayDatetime: undefined,
        tooltipDate: null,
      }
    }

    const displayDatetime = showHumanized
      ? humanize(date, dateFormat, isDateOnly)
      : formatDatetime(date, dateFormat, isDateOnly)
    const tooltipDate = showTooltip ? getTooltipDate({ date, dateFormat }) : null

    return { displayDatetime, tooltipDate }
  }, [date, dateFormat, showHumanized, showTooltip, isDateOnly])

  if (tooltipDate && showHumanized) {
    return (
      <Tooltip title={tooltipDate} position={position}>
        {isUnstyled ? (
          <>{displayDatetime}</>
        ) : (
          <Text {...textProps} textAlign={textAlign} inline>
            {displayDatetime}
          </Text>
        )}
      </Tooltip>
    )
  }

  if (isUnstyled) {
    return <>{displayDatetime}</>
  }

  return (
    <Text {...textProps} textAlign={textAlign} inline>
      {displayDatetime}
    </Text>
  )
}

export default DateWithToolTip
